import type {
  FileUploadRestrictions,
  UploaderFile,
  UploaderFileListRenderProps,
  UploaderMeta,
  UploadSuccessResult,
} from '../types';
import type Uppy from '@uppy/core';
import type { Meta } from '@uppy/core';
import type { ReactNode } from 'react';

import { cn } from 'cn';
import { Hint, Reveal } from '~/atoms';
import { Alert } from '~/molecules';

import { useIsUploaderBusy } from '../utils/useIsUploaderBusy';

import { Dropzone } from './Dropzone';
import { UploadButton } from './UploadButton';
import { UploaderFileList } from './UploaderFileList';

export type UploaderContentOptions<TMeta extends UploaderMeta = UploaderMeta> =
  {
    confirmAfterUpload?: boolean;
    confirmButtonText?: (count: number) => string;
    hintText?: string;
    isFileReady?: (file: UploaderFile<TMeta>) => boolean;
    renderFileList?: (props: UploaderFileListRenderProps<TMeta>) => ReactNode;
    showRemoveAllFiles?: boolean;
    showRestrictionsHint?: boolean;
    uploadButtonText?: string;
  };

type P<TMeta extends UploaderMeta> = {
  dropzoneClassName?: string;
  errorMessage: string | null;
  isConfirming: boolean;
  isDisabled?: boolean;
  onCancel?: () => void;
  onSuccess: (result: UploadSuccessResult<TMeta>) => Promise<void>;
  onUploadError: (error: unknown) => void;
  restrictions?: FileUploadRestrictions;
  uppy: Uppy<Meta, Record<string, never>>;
} & UploaderContentOptions<TMeta> &
  ComponentStyling;

/**
 * The uploader body — hint, error, dropzone, file list and actions — without
 * any surrounding surface, so it can be hosted by a dialog or rendered inline.
 *
 * Expects to be rendered inside an `UppyContextProvider` for `uppy`.
 */
export function UploaderContent<TMeta extends UploaderMeta>({
  className,
  confirmAfterUpload = false,
  confirmButtonText,
  dropzoneClassName = 'h-60',
  errorMessage,
  hintText,
  isConfirming,
  isDisabled = false,
  isFileReady,
  onCancel,
  onSuccess,
  onUploadError,
  renderFileList,
  restrictions,
  showRemoveAllFiles = true,
  showRestrictionsHint = true,
  style,
  uploadButtonText,
  uppy,
}: P<TMeta>) {
  const isBusy = useIsUploaderBusy(uppy, isConfirming);

  return (
    <div
      className={cn('uploader-content flex min-h-0 flex-col', className)}
      style={style}
    >
      <div className="uploader-body min-h-0 overflow-y-auto">
        {Boolean(hintText) && (
          <Hint className="uploader-hint mb-2 block">{hintText}</Hint>
        )}
        <Reveal show={Boolean(errorMessage)}>
          {errorMessage && (
            <Alert className="uploader-error mb-4" severity="error">
              {errorMessage}
            </Alert>
          )}
        </Reveal>
        <Dropzone
          className={dropzoneClassName}
          isDisabled={isDisabled}
          restrictions={restrictions}
          showRestrictionsHint={showRestrictionsHint}
        />
        <UploaderFileList
          isDisabled={isBusy || isDisabled}
          renderFileList={renderFileList}
          uppy={uppy}
        />
      </div>
      <UploadButton
        className="uploader-actions px-0 py-4"
        confirmAfterUpload={confirmAfterUpload}
        confirmButtonText={confirmButtonText}
        isConfirming={isConfirming}
        isDisabled={isDisabled}
        isFileReady={isFileReady}
        onCancel={onCancel}
        onError={onUploadError}
        onSuccess={onSuccess}
        showRemoveAllFiles={showRemoveAllFiles}
        uploadButtonText={uploadButtonText}
        uppy={uppy}
      />
    </div>
  );
}
